import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

export interface FavoriteItem {
  id: number;
  name: string;
  price: number;
  oldPrice?: number;
  image: string;
  color: string;
  category?: string;
  rating?: number;
  discount?: number;
  inStock?: boolean;
  addedAt: number;
}

interface FavoritesContextValue {
  items: FavoriteItem[];
  addFavorite: (item: Omit<FavoriteItem, 'addedAt'>) => void;
  removeFavorite: (id: number) => void;
  toggleFavorite: (item: Omit<FavoriteItem, 'addedAt'>) => void;
  isFavorite: (id: number) => boolean;
  clearFavorites: () => void;
}

interface FavoritesProviderProps {
  children: ReactNode;
  onToggle?: (item: Omit<FavoriteItem, 'addedAt'>, added: boolean) => void;
}

const STORAGE_KEY = 'neonpharma_favorites';

const readStorage = (): FavoriteItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as FavoriteItem[]) : [];
  } catch {
    return [];
  }
};

const writeStorage = (items: FavoriteItem[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
};

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

export const FavoritesProvider = ({ children, onToggle }: FavoritesProviderProps) => {
  const [items, setItems] = useState<FavoriteItem[]>(() => readStorage());

  useEffect(() => {
    const handler = () => setItems(readStorage());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const addFavorite = useCallback((item: Omit<FavoriteItem, 'addedAt'>) => {
    setItems(prev => {
      if (prev.some(i => i.id === item.id)) return prev;
      const updated = [{ ...item, addedAt: Date.now() }, ...prev];
      writeStorage(updated);
      return updated;
    });
  }, []);

  const removeFavorite = useCallback((id: number) => {
    setItems(prev => {
      const updated = prev.filter(i => i.id !== id);
      writeStorage(updated);
      return updated;
    });
  }, []);

  const toggleFavorite = useCallback((item: Omit<FavoriteItem, 'addedAt'>) => {
    setItems(prev => {
      const exists = prev.some(i => i.id === item.id);
      const updated = exists
        ? prev.filter(i => i.id !== item.id)
        : [{ ...item, addedAt: Date.now() }, ...prev];
      writeStorage(updated);
      onToggle?.(item, !exists);
      return updated;
    });
  }, [onToggle]);

  const isFavorite = useCallback((id: number) => items.some(i => i.id === id), [items]);

  const clearFavorites = useCallback(() => {
    setItems([]);
    writeStorage([]);
  }, []);

  return (
    <FavoritesContext.Provider value={{ items, addFavorite, removeFavorite, toggleFavorite, isFavorite, clearFavorites }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used within FavoritesProvider');
  return ctx;
};
